import api from "../../../services/apiClient";
import API_ENDPOINTS from "../../../config/apiEndpoints";
import utils from "../../../utils/utils";
import {
  GROUP_TYPE,
  buildMultiGroupStep3Payload,
  flagsFromStudyRow,
  setupFlagsToStudyRow,
} from "./scheduleWorkflow";
import {
  VISIT_TYPE,
  augmentGroupNamesWithStudyScreening,
  groupVisitsFromApi,
  resolveSharedTreatmentVisitDrafts,
  resolveStudyScreeningGroupId,
  studyScreeningVisitsFromApi,
  treatmentGroupNames,
  treatmentVisitsFromApiRows,
  visitDraftFromApiRow,
  visitsForVisitRail,
  visitsFromApiRows,
  visitsWithoutAdhoc,
} from "./scheduleVisitDraft";
import { SCHEDULE_AUDIT_REASON_MAX_LENGTH } from "./scheduleSaveGate";
import {
  buildIndividualVisitPayload,
  buildScreeningDetailsPayload,
  filterApiVisitsForGroup,
  mergeDraftsWithApiVisits,
  patchApiVisitRowsWithScreeningDrafts,
  validateNonResidentialFields,
  validateResidentialFields,
  validateScreeningFields,
  validateVisitReferenceForSave,
  visitHasPersistableDetails,
} from "./scheduleVisitPersistence";
import { looksLikeActionLabel } from "./scheduleAuditLabels";

const pick = (obj, ...keys) => {
  if (obj == null) return undefined;
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  }
  return undefined;
};

function coerceSqlBit(value) {
  return (
    value === true ||
    value === 1 ||
    value === "1" ||
    value === "true" ||
    value === "True"
  );
}

function screeningRandomisationFromGroupRows(
  screeningGroup = {},
  primaryGroupName = {},
  studyGroup = {},
) {
  return coerceSqlBit(
    pick(screeningGroup, "ScreeningrandomisationTogether", "screeningrandomisationTogether") ||
      pick(
        primaryGroupName,
        "ScreeningrandomisationTogether",
        "screeningrandomisationTogether",
        "StudyScreeningRandomisationTogether",
        "studyScreeningRandomisationTogether",
      ) ||
      pick(
        studyGroup,
        "StudyScreeningRandomisationTogether",
        "studyScreeningRandomisationTogether",
        "ScreeningrandomisationTogether",
        "screeningrandomisationTogether",
      ),
  );
}

function enrichCohortSetupFlags(parsed) {
  if (!parsed || typeof parsed !== "object") return parsed;

  const groupNames = Array.isArray(parsed.groupNames) ? parsed.groupNames : [];
  const screeningGroup = groupNames.find(
    (group) => Number(pick(group, "GroupNo", "groupNo")) === 0,
  );
  const primaryGroupName =
    groupNames.find((group) => Number(pick(group, "GroupNo", "groupNo")) === 1) ||
    treatmentGroupNames(groupNames)[0] ||
    {};

  const screeningRandomisationTogether = screeningRandomisationFromGroupRows(
    screeningGroup,
    primaryGroupName,
    parsed.studyGroup,
  );

  return {
    ...parsed,
    studyGroup: {
      ...(parsed.studyGroup || {}),
      StudyScreeningRandomisationTogether: screeningRandomisationTogether,
      ScreeningrandomisationTogether: screeningRandomisationTogether,
    },
    groupNames: groupNames.map((group) => {
      const groupNo = Number(pick(group, "GroupNo", "groupNo"));
      if (groupNo !== 0 && groupNo !== 1) return group;
      return {
        ...group,
        ScreeningrandomisationTogether: screeningRandomisationTogether,
        StudyScreeningRandomisationTogether: screeningRandomisationTogether,
      };
    }),
  };
}

function getDataset(response) {
  const d = response?.data ?? response?.Data;
  return d && typeof d === "object" ? d : null;
}

function getTableRows(dataset, tableNames = [], tableIndex = -1) {
  if (!dataset || typeof dataset !== "object") return [];

  for (const name of tableNames) {
    const direct = dataset[name] ?? dataset[name?.toLowerCase?.()];
    if (Array.isArray(direct)) return direct;
    if (direct?.Rows && Array.isArray(direct.Rows)) return direct.Rows;
  }

  if (Array.isArray(dataset.Tables)) {
    for (const name of tableNames) {
      const table = dataset.Tables.find(
        (t) =>
          String(t?.TableName || t?.Name || t?.tableName || "")
            .toLowerCase() === String(name).toLowerCase(),
      );
      if (Array.isArray(table?.Rows)) return table.Rows;
    }
    if (tableIndex >= 0 && Array.isArray(dataset.Tables[tableIndex]?.Rows)) {
      return dataset.Tables[tableIndex].Rows;
    }
    const first = dataset.Tables.find((t) => Array.isArray(t?.Rows));
    if (first?.Rows && tableNames.length === 0) return first.Rows;
  }

  const keys = Object.keys(dataset);
  for (const name of tableNames) {
    const ci = keys.find((k) => k.toLowerCase() === name.toLowerCase());
    if (ci && Array.isArray(dataset[ci])) return dataset[ci];
  }

  const positionalKeys = ["Table", "Table1", "Table2", "Table3", "Table4"];
  if (tableIndex >= 0 && tableIndex < positionalKeys.length) {
    const key = positionalKeys[tableIndex];
    const alt = dataset[key] ?? dataset[key?.toLowerCase?.()];
    if (Array.isArray(alt)) return alt;
    if (alt?.Rows && Array.isArray(alt.Rows)) return alt.Rows;
  }

  return [];
}

function isTruthyFlag(value) {
  if (value === true || value === 1 || value === "1") return true;
  const normalized = String(value ?? "").trim().toLowerCase();
  return normalized === "true" || normalized === "yes" || normalized === "live";
}

export function parseScheduleLiveFlag(source = {}) {
  return (
    isTruthyFlag(pick(source, "IsCohortScheduleLive", "isCohortScheduleLive")) ||
    isTruthyFlag(pick(source, "LiveStatus", "liveStatus")) ||
    isTruthyFlag(pick(source, "ScheduleLiveStatus", "scheduleLiveStatus")) ||
    isTruthyFlag(source.isScheduleLive)
  );
}

function hasCohortGroupRow(studyGroup = {}) {
  return !!(
    pick(studyGroup, "StudyGroupId", "studyGroupId", "StudyGroupIdRef", "studyGroupIdRef")
  );
}

export { hasCohortGroupRow };

function rowsToArray(raw) {
  if (raw == null) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "object") {
    const vals = Object.values(raw);
    if (vals.length && typeof vals[0] === "object") return vals;
  }
  return [];
}

export function parseCohortSetupResponse(response) {
  const dataset = getDataset(response);
  const studyRows = getTableRows(dataset, ["Study", "study"], 0);
  const studyGroupRows = getTableRows(dataset, ["StudyGroup", "studyGroup"], 1);
  const groupNameRows = getTableRows(
    dataset,
    ["StudyGroupName", "studyGroupName"],
    2,
  );
  const visitRows = getTableRows(dataset, ["Visit", "visit"], 3);
  const currencyRows = getTableRows(
    dataset,
    ["CurrencySymbol", "currencySymbol"],
    4,
  );

  const study = studyRows[0] || {};
  const studyGroup = studyGroupRows[0] || {};

  const scheduleDoneFlag = !!(
    pick(study, "IsStudyScheduleDone", "isStudyScheduleDone") === true ||
    pick(study, "IsStudyScheduleDone", "isStudyScheduleDone") === 1 ||
    pick(study, "IsStudyScheduleDone", "isStudyScheduleDone") === "1"
  );
  const hasSavedCohort = hasCohortGroupRow(studyGroup);
  const hasScheduleRecord = !!(
    pick(study, "StudyScheduleId", "studyScheduleId") ||
    pick(studyGroup, "StudyIdRef", "studyIdRef")
  );

  return enrichCohortSetupFlags({
    study,
    studyGroup,
    groupNames: groupNameRows,
    visits: visitRows,
    currencySymbol: pick(currencyRows[0], "CurrencySymbol", "currencySymbol") || "£",
    isScheduleDone: scheduleDoneFlag || hasSavedCohort || hasScheduleRecord,
    isScheduleLive: parseScheduleLiveFlag(study),
    trialStudyId:
      pick(study, "StudyIdRef", "studyIdRef") ||
      pick(study, "StudyId", "studyId") ||
      "",
    studyScheduleId:
      pick(study, "StudyScheduleId", "studyScheduleId") ||
      pick(studyGroup, "StudyIdRef", "studyIdRef") ||
      pick(studyGroup, "StudyScheduleId", "studyScheduleId"),
    studyGroupId:
      pick(studyGroup, "StudyGroupId", "studyGroupId") ||
      pick(studyGroup, "StudyGroupIdRef", "studyGroupIdRef"),
    studyName: pick(study, "StudyName", "studyName") || "",
    studyColor:
      pick(study, "StudySchColor", "studySchColor", "StudyColor", "studyColor") ||
      "#2563eb",
    groupType:
      pick(studyGroup, "GroupType", "groupType") || "Single Group",
  });
}

function firstRowArray(response) {
  const data = getDataset(response);
  if (Array.isArray(data)) return data;
  return rowsToArray(data);
}

function firstResultRow(response) {
  const dataset = getDataset(response);
  const named = getTableRows(dataset, ["Table", "table"], 0);
  if (named[0] && typeof named[0] === "object" && !Array.isArray(named[0])) {
    return named[0];
  }
  const rows = firstRowArray(response);
  let row = Array.isArray(rows) ? rows[0] : rows;
  while (Array.isArray(row)) row = row[0];
  return row && typeof row === "object" ? row : {};
}

/** One load on entry — cached by caller; no duplicate fetches inside wizard. */
export async function fetchCohortSetup({ studyId, organizationId }) {
  const response = await api.post(API_ENDPOINTS.ESCHEDULE.GET_COHORT_SETUP, {
    StudyId: studyId,
    OrganizationId: organizationId,
    Action: "GetCohortGroupDetails",
  });
  if (!utils.isApiSuccess(response)) {
    throw new Error(
      utils.getApiResponseMessage(response, "Could not load schedule setup."),
    );
  }
  return parseCohortSetupResponse(response);
}

export async function saveSingleGroupSetup(payload) {
  const response = await api.post(
    API_ENDPOINTS.ESCHEDULE.SINGLE_GROUP_SETUP,
    payload,
  );
  if (!utils.isApiSuccess(response)) {
    throw new Error(
      utils.getApiResponseMessage(response, "Could not save schedule setup."),
    );
  }
  return firstRowArray(response);
}

export async function saveMultiGroupStep1(payload) {
  const response = await api.post(
    API_ENDPOINTS.ESCHEDULE.MULTI_GROUP_STEP1,
    payload,
  );
  if (!utils.isApiSuccess(response)) {
    throw new Error(
      utils.getApiResponseMessage(response, "Could not save group setup."),
    );
  }
  return firstRowArray(response);
}

export async function saveMultiGroupRunIn(payload) {
  const response = await api.post(
    API_ENDPOINTS.ESCHEDULE.MULTI_GROUP_RUNIN,
    payload,
  );
  if (!utils.isApiSuccess(response)) {
    throw new Error(
      utils.getApiResponseMessage(response, "Could not save run-in period."),
    );
  }
  return firstRowArray(response);
}

export async function saveMultiGroupStep2(payload) {
  const response = await api.post(
    API_ENDPOINTS.ESCHEDULE.MULTI_GROUP_STEP2,
    payload,
  );
  if (!utils.isApiSuccess(response)) {
    throw new Error(
      utils.getApiResponseMessage(response, "Could not create study groups."),
    );
  }
  return firstRowArray(response);
}

export async function applyMultiGroupVisits(payload) {
  const response = await api.post(
    API_ENDPOINTS.ESCHEDULE.MULTI_GROUP_STEP3,
    payload,
  );
  if (!utils.isApiSuccess(response)) {
    throw new Error(
      utils.getApiResponseMessage(response, "Could not apply visits."),
    );
  }
  return firstRowArray(response);
}

export async function publishScheduleLive({
  studyScheduleId,
  studyId,
  organizationId,
  createdBy,
  status = "1",
  comments,
}) {
  const response = await api.post(API_ENDPOINTS.ESCHEDULE.SCHEDULE_LIVE, {
    StudyScheduleId: studyScheduleId,
    StudyId: studyId,
    OrganizationId: organizationId,
    CreatedBy: createdBy,
    Status: status,
    ...(comments ? { Comments: comments } : {}),
  });
  if (!utils.isApiSuccess(response)) {
    throw new Error(
      utils.getApiResponseMessage(response, "Could not publish schedule."),
    );
  }
  return response;
}

export function mergeCohortAfterPublish(cohortState) {
  if (!cohortState) return cohortState;
  return {
    ...cohortState,
    isScheduleLive: true,
    study: {
      ...(cohortState.study || {}),
      IsCohortScheduleLive: 1,
      isCohortScheduleLive: 1,
    },
  };
}

export function patchGroupNamesWithSetupFlags(groupNames = [], setupFlags = null) {
  if (!setupFlags || !groupNames.length) return groupNames;
  const flagPatch = setupFlagsToStudyRow(setupFlags);
  return groupNames.map((group) => {
    const groupNo = Number(pick(group, "GroupNo", "groupNo"));
    return {
      ...group,
      ...(groupNo === 1 ? { GroupScreening: flagPatch.IsStudyScreening } : null),
      ScreeningrandomisationTogether: flagPatch.ScreeningrandomisationTogether,
      StudyScreeningRandomisationTogether:
        flagPatch.StudyScreeningRandomisationTogether,
    };
  });
}

export function mergeCohortFromSave(cohortState, saveRows = [], setupFlags = null) {
  const row = saveRows[0] || {};
  const flagPatch = setupFlags ? setupFlagsToStudyRow(setupFlags) : {};
  const studyScheduleId =
    pick(row, "StudyScheduleId", "studyScheduleId") ||
    pick(row, "StudyIdRef", "studyIdRef") ||
    cohortState?.studyScheduleId;
  return {
    ...cohortState,
    studyGroupId:
      pick(row, "StudyGroupId", "studyGroupId") || cohortState?.studyGroupId,
    studyScheduleId,
    isScheduleDone: true,
    groupNames: patchGroupNamesWithSetupFlags(
      cohortState?.groupNames || [],
      setupFlags,
    ),
    study: {
      ...(cohortState?.study || {}),
      ...flagPatch,
      StudyScheduleId:
        studyScheduleId ||
        cohortState?.study?.StudyScheduleId,
    },
    studyGroup: {
      ...(cohortState?.studyGroup || {}),
      ...flagPatch,
      StudyGroupId:
        pick(row, "StudyGroupId", "studyGroupId") || cohortState?.studyGroup?.StudyGroupId,
    },
  };
}

export function visitPreviewRows(visits = [], noOfVisits = 0) {
  if (visits.length) return visits;
  const count = Math.max(0, Number(noOfVisits) || 0);
  return Array.from({ length: count }, (_, i) => ({
    VisitName: `Visit ${i + 1}`,
    VisitOrder: i + 1,
  }));
}

function normalizeScheduleAuditRows(rows) {
  const list = Array.isArray(rows) ? rows : [];
  return list.map((r, index) => {
    const rawAction = String(r?.Action ?? r?.action ?? "").trim();
    const rawNew = String(r?.NewValue ?? r?.newValue ?? "").trim();
    const rawComments = String(
      r?.Comments ?? r?.comments ?? r?.reasonForRevisionRef ?? "",
    ).trim();
    const sourceLevel = r?.SourceLevel || r?.sourceLevel || "";
    const fieldName =
      r?.FieldName || r?.fieldName || r?.ColumnName || r?.columnAffected || "";

    // SP Action is the label; legacy rows may store the label in NewValue instead.
    let action = rawAction || "Update";
    if (looksLikeActionLabel(rawAction)) {
      action = rawAction;
    } else if (looksLikeActionLabel(rawNew)) {
      action = rawNew;
    } else if (rawNew) {
      action = rawNew;
    } else {
      action = rawAction || "Update";
    }
    const comments = rawComments;

    return {
      ...r,
      Id:
        r?.Id ||
        r?.id ||
        r?.AuditId ||
        r?.auditId ||
        `audit-${index}-${String(action || "update").slice(0, 24)}`,
      At: r?.At || r?.at || r?.timeCommitted || r?.PerformedAt || null,
      UserName:
        r?.UserName ||
        r?.userName ||
        r?.PerformedByName ||
        r?.performedByName ||
        "",
      Action: action,
      FieldName: fieldName,
      OldValue: r?.OldValue ?? r?.oldValue ?? "",
      NewValue: rawNew,
      Comments: comments,
      SourceLevel: sourceLevel,
    };
  });
}

export async function fetchScheduleAuditLog({
  trialStudyId,
  studyScheduleId,
  organizationId,
}) {
  if (!trialStudyId || !organizationId) return [];
  const response = await api.post(API_ENDPOINTS.ESCHEDULE.GET_AUDIT_LOG, {
    TrialStudyId: trialStudyId,
    StudyScheduleId: studyScheduleId || null,
    OrganizationId: organizationId,
  });
  if (!utils.isApiSuccess(response)) {
    throw new Error(
      utils.getApiResponseMessage(response, "Could not load schedule activity."),
    );
  }
  const data = getDataset(response);
  const rows = getTableRows(data, ["AuditLog", "auditLog"]);
  if (Array.isArray(data) && !rows.length) {
    return normalizeScheduleAuditRows(data);
  }
  return normalizeScheduleAuditRows(rows);
}

export async function writeEcommSaveAudit({
  trialStudyId,
  studyScheduleId,
  organizationId,
  createdBy,
  templates,
  activeTemplateType,
  visitName = "",
  beforeTemplate = null,
  onAuditWritten = null,
}) {
  const active =
    (templates || []).find((t) => t.TemplateType === activeTemplateType) ||
    templates?.[0];
  if (!active || !organizationId || !createdBy) {
    throw new Error("Activity log could not be recorded (missing visit context).");
  }

  const scheduleId = studyScheduleId || trialStudyId;
  const trialId = trialStudyId || studyScheduleId;
  if (!scheduleId || !trialId) {
    throw new Error("Activity log could not be recorded (missing study id).");
  }

  const { buildEcommTemplateAuditPayload, SCHEDULE_AUDIT_COLUMNS } = await import(
    "./scheduleAuditEvents"
  );
  const auditPayload = buildEcommTemplateAuditPayload({
    beforeTemplate,
    afterTemplate: active,
    templateType: active.TemplateType || activeTemplateType,
    visitName,
  });

  await writeScheduleAuditLog({
    trialStudyId: trialId,
    studyScheduleId: scheduleId,
    organizationId,
    createdBy,
    columnName: SCHEDULE_AUDIT_COLUMNS.VISITS,
    ...auditPayload,
  });
  await onAuditWritten?.();
}

function trimAuditPayloadValue(value = "", max = 2000) {
  const text = String(value || "").trim();
  if (!text || text.length <= max) return text;
  return `${text.slice(0, max)}…`;
}

export async function writeScheduleAuditLog({
  trialStudyId,
  studyScheduleId,
  organizationId,
  createdBy,
  actionLabel,
  comments = "",
  columnName = "ScheduleVisits",
  oldValue = "",
  newValue = "",
}) {
  if (!trialStudyId || !organizationId || !createdBy || !actionLabel) {
    throw new Error("Schedule activity could not be recorded (missing context).");
  }
  const response = await api.post(API_ENDPOINTS.ESCHEDULE.WRITE_AUDIT_LOG, {
    TrialStudyId: trialStudyId,
    StudyScheduleId: studyScheduleId || null,
    OrganizationId: organizationId,
    CreatedBy: createdBy,
    ActionLabel: actionLabel,
    Comments: String(comments || "").trim().slice(0, SCHEDULE_AUDIT_REASON_MAX_LENGTH),
    ColumnName: columnName,
    OldValue: trimAuditPayloadValue(oldValue),
    NewValue: trimAuditPayloadValue(newValue),
  });
  if (!utils.isApiSuccess(response)) {
    throw new Error(
      utils.getApiResponseMessage(response, "Could not record schedule activity."),
    );
  }
  return response;
}

export function getVisitApiErrorMessage(response, fallback) {
  const top = String(response?.message || response?.Message || "").trim();
  const data = response?.data;
  const rows =
    data?.SuccessStory ||
    data?.successStory ||
    (Array.isArray(data) ? data : null);
  const rowMsg =
    Array.isArray(rows) && rows.length > 0
      ? rows[0]?.MSG || rows[0]?.Msg || rows[0]?.message || rows[0]?.Message
      : null;
  const rowText = rowMsg && String(rowMsg).trim() ? String(rowMsg).trim() : "";

  if (rowText) {
    return utils.sanitizeUserFacingMessage(rowText, fallback);
  }

  if (top.toLowerCase() === "no list found") {
    return "Visit action failed. Deploy the latest SQL/StudyManagement scripts and restart the API.";
  }

  if (top.toLowerCase() === "invalid request" && rowText) {
    return utils.sanitizeUserFacingMessage(rowText, fallback);
  }

  return utils.getApiResponseMessage(response, fallback);
}

async function postVisitApi(endpoint, payload, errorMessage) {
  const response = await api.post(endpoint, payload);
  if (!utils.isApiSuccess(response)) {
    throw new Error(getVisitApiErrorMessage(response, errorMessage));
  }
  return response;
}

export async function saveScreeningVisitDetails(payload) {
  return postVisitApi(
    API_ENDPOINTS.ESCHEDULE.SCREENING_DETAILS,
    payload,
    "Could not save screening visit details.",
  );
}

export async function saveIndividualVisitDetails(payload) {
  return postVisitApi(
    API_ENDPOINTS.ESCHEDULE.INDIVIDUAL_VISIT,
    payload,
    "Could not save visit details.",
  );
}

export async function changeVisitName({
  studyScheduleId,
  visit,
  organizationId,
  createdBy,
}) {
  // Legacy MVC uses IndividualVisitDetails with Action=ChangeVisitName (not the
  // separate usp_ESch_ChangeVisitName_NEWUI endpoint).
  return saveIndividualVisitDetails({
    StudyId: studyScheduleId,
    VisitId: visit.visitsPerStudyId,
    VisitName: visit.visitName,
    OrganizationId: organizationId,
    CreatedBy: createdBy,
    Action: "ChangeVisitName",
    TypeOfVisit: "",
    ResidentialVisitStart: "",
    ResidentialVisitEnd: "",
    ResidentialVisitDuration: "",
    ResidentialVisitTolerence: "",
    NonResidentialVisitTypeId: "",
    NonResidentialVisitOccure: "",
    NonResiVisitTolerence: "",
    RefVisitId: "",
    UpperTolerenceLimit: "",
  });
}

export async function removeVisitFromStudy({
  studyScheduleId,
  visit,
  organizationId,
  createdBy,
}) {
  if (!visit?.visitsPerStudyId) {
    throw new Error("This visit is not saved yet.");
  }
  return saveIndividualVisitDetails(
    buildIndividualVisitPayload({
      studyScheduleId,
      visit,
      organizationId,
      createdBy,
      action: "RemoveVisit",
    }),
  );
}

/** Legacy + button on visit rail — inserts after the reference visit with a chosen name. */
export async function addAdjacentVisitToStudy({
  studyScheduleId,
  referenceVisit,
  visitName,
  organizationId,
  createdBy,
}) {
  if (!referenceVisit?.visitsPerStudyId) {
    throw new Error("Select a saved visit to add next to.");
  }
  const name = String(visitName || "").trim();
  if (!name) {
    throw new Error("Enter a visit name.");
  }
  return saveIndividualVisitDetails({
    StudyId: studyScheduleId,
    VisitId: referenceVisit.visitsPerStudyId,
    VisitName: name,
    OrganizationId: organizationId,
    CreatedBy: createdBy,
    Action: "AddAdjacentVisit",
    TypeOfVisit: "",
    ResidentialVisitStart: "",
    ResidentialVisitEnd: "",
    ResidentialVisitDuration: "",
    ResidentialVisitTolerence: "",
    NonResidentialVisitTypeId: "",
    NonResidentialVisitOccure: "",
    NonResiVisitTolerence: "",
    RefVisitId: "",
    UpperTolerenceLimit: "",
  });
}

export async function swapVisitOrder({
  studyScheduleId,
  visit,
  direction,
  organizationId,
  createdBy,
}) {
  if (!visit?.visitsPerStudyId) {
    throw new Error("This visit is not saved yet.");
  }
  const normalized = String(direction || "").toLowerCase();
  if (normalized !== "up" && normalized !== "down") {
    throw new Error("Invalid visit move direction.");
  }
  return postVisitApi(
    API_ENDPOINTS.ESCHEDULE.SWAP_VISIT_ORDER,
    {
      StudyId: studyScheduleId,
      VisitId: visit.visitsPerStudyId,
      Direction: normalized === "up" ? "Up" : "Down",
      OrganizationId: organizationId,
      CreatedBy: createdBy,
    },
    "Could not reorder visit.",
  );
}

export async function syncVisitCountInsertVisit({
  studyScheduleId,
  organizationId,
  createdBy,
  noOfVisits,
  studyColor = null,
  groupNameIdRef = null,
}) {
  const response = await api.post(API_ENDPOINTS.ESCHEDULE.VISITS_PER_STUDY, {
    StudyId: studyScheduleId,
    NoOfVisits: Number(noOfVisits),
    OrganizationId: organizationId,
    CreatedBy: createdBy,
    Action: "InsertVisit",
    StudyColor: studyColor || null,
    GroupNameIdRef: groupNameIdRef || null,
  });
  if (!utils.isApiSuccess(response)) {
    throw new Error(
      utils.getApiResponseMessage(response, "Could not save visit structure."),
    );
  }
  return response;
}

export async function syncVisitStructure({
  trialStudyId,
  studyScheduleId,
  studyGroupId,
  organizationId,
  createdBy,
  noOfVisits,
  setupFlags = {},
  studyColor = null,
  groupNameId = null,
  groupType = GROUP_TYPE.SINGLE,
  allGroupsSameSchedule = true,
  isStudyScreeningGroup = false,
}) {
  const isMulti = groupType === GROUP_TYPE.MULTIPLE && studyGroupId;

  if (isMulti && isStudyScreeningGroup) {
    return { mode: "study_screening" };
  }

  if (isMulti) {
    const isMultiSeparate = !allGroupsSameSchedule && groupNameId;
    await applyMultiGroupVisits(
      buildMultiGroupStep3Payload({
        studyId: trialStudyId,
        studyScheduleId,
        organizationId,
        createdBy,
        studyGroupId,
        groupNameId: isMultiSeparate ? groupNameId : null,
        noOfVisits,
        setupFlags,
      }),
    );
    return { mode: "multi" };
  }

  await syncVisitCountInsertVisit({
    studyScheduleId,
    organizationId,
    createdBy,
    noOfVisits,
    studyColor,
  });
  return { mode: "insert" };
}

export async function persistOneVisitDetails({
  studyScheduleId,
  organizationId,
  createdBy,
  visit,
  visits = [],
  screening = false,
  section = "time_point",
}) {
  if (!visit?.visitsPerStudyId) {
    throw new Error("This visit is not in the database yet. Add the visit first.");
  }

  const label = visit.visitName || "Visit";
  const ctx = {
    studyScheduleId,
    visit,
    organizationId,
    createdBy,
    visits,
  };
  const isScreening = visit.visitType === VISIT_TYPE.SCREENING || visit.isScreening;

  if (section === "visit_name") {
    const name = String(visit.visitName || "").trim();
    if (!name) {
      throw new Error("Enter a visit name.");
    }
    if (!isScreening && name.toLowerCase() === "screening") {
      throw new Error("Visit name cannot be Screening.");
    }
    await changeVisitName(ctx);
    return;
  }

  if (section === "screening" || isScreening) {
    if (!screening) {
      throw new Error("Screening is not enabled for this study.");
    }
    const check = validateScreeningFields(visit, label);
    if (!check.valid) throw new Error(check.message);
    await saveScreeningVisitDetails(buildScreeningDetailsPayload(ctx));
    return;
  }

  if (section === "time_point") {
    const check =
      visit.visitType === VISIT_TYPE.RESIDENTIAL
        ? validateResidentialFields(visit, label)
        : validateNonResidentialFields(visit, label);
    if (!check.valid) throw new Error(check.message);
    const refCheck = validateVisitReferenceForSave(visit, visits);
    if (!refCheck.valid) throw new Error(refCheck.message);
    await saveIndividualVisitDetails(buildIndividualVisitPayload(ctx));
  }
}

export async function saveUpdateDeleteGroup(payload) {
  const response = await api.post(
    API_ENDPOINTS.ESCHEDULE.SAVE_UPDATE_DELETE_GROUP,
    payload,
  );
  if (!utils.isApiSuccess(response)) {
    throw new Error(
      utils.getApiResponseMessage(response, "Could not update group."),
    );
  }
  return firstRowArray(response);
}

export async function cloneCohortScheduleToStudy({
  sourceStudyScheduleId,
  targetTrialStudyId,
  organizationId,
  createdBy,
  studyColor,
}) {
  const response = await api.post(API_ENDPOINTS.ESCHEDULE.CLONE_SCHEDULE_TO_STUDY, {
    SourceStudyScheduleId: sourceStudyScheduleId,
    TargetTrialStudyId: targetTrialStudyId,
    OrganizationId: organizationId,
    CreatedBy: createdBy,
    StudyColor: studyColor || undefined,
  });
  if (!utils.isApiSuccess(response)) {
    throw new Error(
      utils.getApiResponseMessage(response, "Could not clone schedule."),
    );
  }
  return firstResultRow(response);
}

export async function persistVisitDraftBatch({
  studyScheduleId,
  organizationId,
  createdBy,
  visits = [],
  screening = false,
}) {
  for (const visit of visits) {
    if (!visit?.visitsPerStudyId) continue;

    const isScreening = visit.visitType === "screening" || visit.isScreening;
    if (isScreening) {
      if (!visitHasPersistableDetails(visit, { screening })) continue;
      await saveScreeningVisitDetails(
        buildScreeningDetailsPayload({
          studyScheduleId,
          visit,
          organizationId,
          createdBy,
        }),
      );
      continue;
    }

    if (!visitHasPersistableDetails(visit, { screening })) continue;
    await saveIndividualVisitDetails(
      buildIndividualVisitPayload({
        studyScheduleId,
        visit,
        organizationId,
        createdBy,
        visits,
      }),
    );
  }
}

function resolveSetupFlagsFromCohort(parsed, prevWizard = {}) {
  const augmentedGroupNames = augmentGroupNamesWithStudyScreening(
    parsed.groupNames,
    parsed.visits,
    parsed.studyGroup,
  );
  const screeningGroup = augmentedGroupNames.find(
    (group) => Number(pick(group, "GroupNo", "groupNo")) === 0,
  );
  const primaryGroupName =
    augmentedGroupNames.find(
      (group) => Number(pick(group, "GroupNo", "groupNo")) === 1,
    ) ||
    treatmentGroupNames(augmentedGroupNames)[0] ||
    {};
  const studyGroupFlags = flagsFromStudyRow(
    { ...parsed.study, ...parsed.studyGroup },
    primaryGroupName,
  );

  const screeningRandomisationTogether = screeningRandomisationFromGroupRows(
    screeningGroup,
    primaryGroupName,
    parsed.studyGroup,
  ) || studyGroupFlags.screeningRandomisationTogether;

  return {
    ...studyGroupFlags,
    screeningRandomisationTogether,
  };
}

export function buildWizardPatchFromCohort(parsed, prevWizard = {}) {
  const augmentedGroupNames = augmentGroupNamesWithStudyScreening(
    parsed.groupNames,
    parsed.visits,
    parsed.studyGroup,
  );
  const primaryGroupName =
    augmentedGroupNames.find(
      (group) => Number(pick(group, "GroupNo", "groupNo")) === 1,
    ) ||
    treatmentGroupNames(augmentedGroupNames)[0] ||
    {};
  const setupFlags = resolveSetupFlagsFromCohort(parsed, prevWizard);
  const isMultiGroup =
    (parsed.groupType || prevWizard.groupType) === GROUP_TYPE.MULTIPLE;
  const screening = setupFlags.studyScreening;
  const apiVisitDrafts = isMultiGroup
    ? treatmentVisitsFromApiRows(parsed.visits).map((row, index) =>
        visitDraftFromApiRow(row, index),
      )
    : visitsFromApiRows(parsed.visits);
  const apiGroupVisitDrafts = groupVisitsFromApi(parsed.visits, augmentedGroupNames);
  const primaryGroupId =
    pick(primaryGroupName, "GroupNameId", "groupNameId") ||
    pick(primaryGroupName, "GroupNameIdRef", "groupNameIdRef") ||
    null;
  const normalizedApiVisits = isMultiGroup
    ? resolveSharedTreatmentVisitDrafts({
        visitDrafts: apiVisitDrafts,
        groupVisitDrafts: apiGroupVisitDrafts,
        apiVisits: parsed.visits,
        primaryGroupId,
      })
    : visitsForVisitRail(apiVisitDrafts);
  const normalizedGroupVisits = Object.fromEntries(
    Object.entries(apiGroupVisitDrafts).map(([key, rows]) => [
      key,
      visitsForVisitRail(rows),
    ]),
  );

  if (isMultiGroup && screening) {
    const screeningGroupId = resolveStudyScreeningGroupId(parsed.visits, augmentedGroupNames);
    if (screeningGroupId && !normalizedGroupVisits[screeningGroupId]?.length) {
      const screeningRows = studyScreeningVisitsFromApi(parsed.visits, screeningGroupId);
      normalizedGroupVisits[screeningGroupId] = visitsForVisitRail(screeningRows);
    }
  }

  return {
    groupType: parsed.groupType || prevWizard.groupType,
    studyColor: parsed.studyColor || prevWizard.studyColor,
    setupFlags,
    noOfGroups: Math.max(
      Number(parsed.studyGroup?.TotalGroup || parsed.studyGroup?.totalGroup) ||
        prevWizard.noOfGroups,
      2,
    ),
    allGroupsSameSchedule:
      parsed.studyGroup?.AllGroupSameSchedule ??
      parsed.studyGroup?.allGroupSameSchedule ??
      prevWizard.allGroupsSameSchedule,
    runInPeriod: !!(
      parsed.studyGroup?.IsRunInPeriod ?? parsed.studyGroup?.isRunInPeriod
    ),
    visitDrafts: apiVisitDrafts.length
      ? normalizedApiVisits
      : prevWizard.visitDrafts,
    groupVisitDrafts:
      Object.keys(normalizedGroupVisits).length > 0
        ? normalizedGroupVisits
        : prevWizard.groupVisitDrafts,
    noOfVisits: Math.max(
      1,
      visitsWithoutAdhoc(visitsForVisitRail(normalizedApiVisits)).length ||
        parsed.visits?.length ||
        prevWizard.noOfVisits,
    ),
    groupNames: augmentedGroupNames.length ? augmentedGroupNames : prevWizard.groupNames,
    activeGroupNameId:
      pick(primaryGroupName, "GroupNameId", "groupNameId") ||
      pick(primaryGroupName, "GroupNameIdRef", "groupNameIdRef") ||
      prevWizard.activeGroupNameId,
  };
}

export function mergeDraftsAfterVisitReload({
  userDrafts = [],
  apiRows = [],
  screening = false,
  groupNameId = null,
}) {
  const scopedRows = filterApiVisitsForGroup(apiRows, groupNameId);
  const patchedRows = patchApiVisitRowsWithScreeningDrafts(scopedRows, userDrafts);
  return mergeDraftsWithApiVisits(userDrafts, patchedRows, { screening });
}

export { mergeDraftsWithApiVisits, visitHasPersistableDetails };
